type PathPoint = { x: number; y: number };
type CprLabelMap = {
  width: number;
  height: number;
  spacingX?: number;
  spacingY?: number;
  data: Uint8Array;
};

export const screenPointToLabelMapPixel = ({
  screenPoint,
  labelMap,
  baseCorners,
  offset,
}: {
  screenPoint: PathPoint;
  labelMap: CprLabelMap | null | undefined;
  baseCorners: PathPoint[] | null | undefined;
  offset: PathPoint;
}) => {
  if (!labelMap || !baseCorners || baseCorners.length < 3) return null;

  const p0 = {
    x: baseCorners[0].x + offset.x,
    y: baseCorners[0].y + offset.y,
  };
  const p1 = {
    x: baseCorners[1].x + offset.x,
    y: baseCorners[1].y + offset.y,
  };
  const p2 = {
    x: baseCorners[2].x + offset.x,
    y: baseCorners[2].y + offset.y,
  };

  const ax = (p1.x - p0.x) / Math.max(1, labelMap.width);
  const ay = (p1.y - p0.y) / Math.max(1, labelMap.width);
  const bx = (p2.x - p0.x) / Math.max(1, labelMap.height);
  const by = (p2.y - p0.y) / Math.max(1, labelMap.height);
  const det = ax * by - ay * bx;
  if (!Number.isFinite(det) || Math.abs(det) < 1e-6) return null;

  const dx = screenPoint.x - p0.x;
  const dy = screenPoint.y - p0.y;
  const x = (dx * by - dy * bx) / det;
  const y = (ax * dy - ay * dx) / det;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return {
    x,
    y,
    col: Math.floor(x),
    row: Math.floor(y),
  };
};

export const collectCprPixelBrushMaskIndices = ({
  screenPoint,
  labelMap,
  baseCorners,
  offset,
  radiusPx,
}: {
  screenPoint: PathPoint;
  labelMap: CprLabelMap | null | undefined;
  baseCorners: PathPoint[] | null | undefined;
  offset: PathPoint;
  radiusPx: number;
}) => {
  const local = screenPointToLabelMapPixel({
    screenPoint,
    labelMap,
    baseCorners,
    offset,
  });
  if (!labelMap || !baseCorners || baseCorners.length < 3 || !local) {
    return [] as number[];
  }

  const p0 = {
    x: baseCorners[0].x + offset.x,
    y: baseCorners[0].y + offset.y,
  };
  const p1 = {
    x: baseCorners[1].x + offset.x,
    y: baseCorners[1].y + offset.y,
  };
  const p2 = {
    x: baseCorners[2].x + offset.x,
    y: baseCorners[2].y + offset.y,
  };

  const ax = (p1.x - p0.x) / Math.max(1, labelMap.width);
  const ay = (p1.y - p0.y) / Math.max(1, labelMap.width);
  const bx = (p2.x - p0.x) / Math.max(1, labelMap.height);
  const by = (p2.y - p0.y) / Math.max(1, labelMap.height);
  const colScreenStep = Math.max(1e-6, Math.hypot(ax, ay));
  const rowScreenStep = Math.max(1e-6, Math.hypot(bx, by));
  const screenRadius = Math.max(0.5, radiusPx);
  const colRadius = Math.ceil(screenRadius / colScreenStep) + 1;
  const rowRadius = Math.ceil(screenRadius / rowScreenStep) + 1;
  const minCol = Math.max(0, Math.floor(local.x - colRadius));
  const maxCol = Math.min(labelMap.width - 1, Math.ceil(local.x + colRadius));
  const minRow = Math.max(0, Math.floor(local.y - rowRadius));
  const maxRow = Math.min(labelMap.height - 1, Math.ceil(local.y + rowRadius));
  const radius2 = screenRadius * screenRadius;
  const indices: number[] = [];

  for (let row = minRow; row <= maxRow; row += 1) {
    for (let col = minCol; col <= maxCol; col += 1) {
      const px = p0.x + (col + 0.5) * ax + (row + 0.5) * bx;
      const py = p0.y + (col + 0.5) * ay + (row + 0.5) * by;
      const dx = px - screenPoint.x;
      const dy = py - screenPoint.y;
      if (dx * dx + dy * dy > radius2) continue;
      indices.push(row * labelMap.width + col);
    }
  }
  return indices;
};

export const applyCprPixelBrushMaskEdit = ({
  labelMap,
  indices,
  value,
  onTouchedIndex,
}: {
  labelMap: CprLabelMap;
  indices: number[];
  value: number;
  onTouchedIndex?: (idx: number) => void;
}) => {
  const nextValue = value === 255 ? 255 : 0;
  const changedIndices: number[] = [];

  for (const idx of indices) {
    if (idx < 0 || idx >= labelMap.data.length) continue;
    if (labelMap.data[idx] === nextValue) continue;
    labelMap.data[idx] = nextValue;
    changedIndices.push(idx);
    onTouchedIndex?.(idx);
  }

  return changedIndices;
};
